/**
 * Centralized date/time formatting for TradesMatePro.
 * All schedule-related display should use these helpers for consistency.
 *
 * Standard format:  "8:05 AM"  (not "08:05 AM")
 * Standard date:    "Mon, Feb 3"
 * Full date+time:   "Mon, Feb 3 · 8:05 AM – 10:30 AM"
 */

// ─── Company timezone ────────────────────────────────────────────────────────
// `company_settings.timezone` was a write-only setting: editable in Business Settings and
// read by nothing, so every date/time in the app rendered in the BROWSER's timezone. A
// dispatcher working from a different timezone than the company saw a different "today"
// than the company's actual day.
//
// Set during the UserContext bootstrap, immediately after the company is resolved and BEFORE
// anything renders a date — see contexts/UserContext.js step 4b, which calls the company_tz()
// RPC (migration 20260813f). Read by every helper here, the same pattern as
// dayjs.tz.setDefault / moment.tz.setDefault.
//
// HISTORY — do not re-break this. Until 2026-08-13 this comment claimed UserContext set it, but
// nothing did: the only caller of setCompanyTimeZone in the entire repo was
// SettingsService.getBusinessSettings(), which is lazy and cached, so it fired only if some
// unrelated component happened to ask for business settings. Until then this global was
// undefined and withTz() below passed `timeZone: undefined`, meaning every one of the ~248
// supposedly timezone-aware call sites in this app silently formatted in the BROWSER's zone.
// That fallback is invisible on a dev machine already in the company's zone, which is precisely
// why "timezone bugs" kept recurring after being "fixed" — the helpers were correct, the
// plumbing was never connected. If you find this unset again, fix the bootstrap; do NOT add a
// second setter somewhere convenient.
let companyTimeZone;

/** Set the active company's IANA timezone (e.g. 'America/New_York'). */
export const setCompanyTimeZone = (tz) => {
  companyTimeZone = tz || undefined;
};

/** The active company timezone, or undefined to mean "use the browser's". */
export const getCompanyTimeZone = () => companyTimeZone;

// DATE AND TIME FORMAT — same story as the timezone above, and published from the same
// place. Business Settings offers a Date Format picker (MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD,
// MMM DD, YYYY) and a 12/24-hour choice; both columns existed, both saved, both reloaded
// into the form correctly, and NOTHING read them -- every date in the app was hardcoded
// 'en-US'. A contractor could set DD/MM/YYYY and watch nothing change.
//
// Expressed as a locale rather than a hand-rolled formatter so month names, era handling and
// numbering all stay Intl's problem. en-GB is day-first, en-CA is ISO-ish, en-US is
// month-first; 'MMM DD, YYYY' is a shape rather than an order, so it carries explicit options.
let companyDateFormat;
let companyTimeFormat;

const DATE_FORMATS = {
  'MM/DD/YYYY': { locale: 'en-US', options: { month: '2-digit', day: '2-digit', year: 'numeric' } },
  'DD/MM/YYYY': { locale: 'en-GB', options: { day: '2-digit', month: '2-digit', year: 'numeric' } },
  'YYYY-MM-DD': { locale: 'en-CA', options: { year: 'numeric', month: '2-digit', day: '2-digit' } },
  'MMM DD, YYYY': { locale: 'en-US', options: { month: 'short', day: '2-digit', year: 'numeric' } }
};

/** Set the company's preferred date format, e.g. 'DD/MM/YYYY'. Unknown values are ignored. */
export const setCompanyDateFormat = (format) => {
  companyDateFormat = DATE_FORMATS[format] ? format : undefined;
};

/** Set the company's clock: '24' for 24-hour, anything else means 12-hour. */
export const setCompanyTimeFormat = (format) => {
  companyTimeFormat = format ? String(format) : undefined;
};

export const getCompanyDateFormat = () => companyDateFormat;
export const getCompanyTimeFormat = () => companyTimeFormat;

/** True when the company has chosen a 24-hour clock. */
const prefers24Hour = () => companyTimeFormat === '24' || companyTimeFormat === '24h';

/** Options with the company timezone applied, when one is known. */
const withTz = (options) => (companyTimeZone ? { ...options, timeZone: companyTimeZone } : options);

/**
 * The calendar date, in the COMPANY's timezone, as 'YYYY-MM-DD'.
 * Use this for any "is this today?" / date-bucketing comparison — never
 * `new Date().toLocaleDateString()`, which buckets by wherever the browser happens to be.
 */
export const companyDateKey = (ts = new Date()) => {
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', withTz({}));
};

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A value bound for a `date` column, as the company's calendar day 'YYYY-MM-DD'.
 * A value that is already a calendar date ('2026-10-19', what <input type="date"> emits and
 * what a `date` column returns) passes through untouched — it has no instant to convert.
 * Anything else (a Date, an ISO timestamp) is an instant and is read on the company calendar.
 *
 * Use this — never `toISOString().split('T')[0]` (the UTC day: tomorrow, every Pacific evening
 * after 5pm) and never the browser's own getDate() (the viewer's calendar, not the company's).
 */
export const toCompanyDateKey = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && DATE_KEY.test(value)) return value;
  return companyDateKey(value);
};

/**
 * The calendar day carried by a Date that ENCODES A CALENDAR DATE AT UTC MIDNIGHT —
 * `new Date('2026-10-19')`, and anything derived from it with the UTC setters (the recurring
 * billing/visit cursors). For those, the UTC day IS the date; there is no instant to convert.
 *
 * NOT for "now" or any real timestamp: that is companyDateKey / toCompanyDateKey. This exists
 * so the one legitimate use of a UTC day is named, and every bare `toISOString().slice(0,10)`
 * left in the app is a bug.
 */
export const utcDateKey = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && DATE_KEY.test(value)) return value;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

/**
 * Calendar arithmetic on a 'YYYY-MM-DD' day: the day `days` later (negative = earlier).
 * Pure date-only math in UTC on both ends, so the answer is the same in every browser
 * timezone and across DST changes — 2026-10-19 + 30 is 2026-11-18 everywhere.
 */
export const addDaysToDateKey = (dateKey, days) => {
  const [y, m, d] = String(dateKey || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d || !Number.isFinite(Number(days))) return null;
  return new Date(Date.UTC(y, m - 1, d + Number(days))).toISOString().slice(0, 10);
};

/**
 * Calendar arithmetic on a 'YYYY-MM-DD' day: the same day-of-month `months` later. Overflow
 * follows Date's own rule (Jan 31 + 1 month = Mar 3 in a non-leap year), i.e. what the
 * `date.setMonth(date.getMonth() + n)` it replaces did, minus the browser timezone.
 */
export const addMonthsToDateKey = (dateKey, months) => {
  const [y, m, d] = String(dateKey || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d || !Number.isFinite(Number(months))) return null;
  return new Date(Date.UTC(y, m - 1 + Number(months), d)).toISOString().slice(0, 10);
};

/**
 * The wall-clock time, in the COMPANY's timezone, as 'HH:mm' — the value shape an
 * `<input type="time">` wants. The counterpart to companyDateKey above.
 *
 * Board #3349. The pair this replaces was `new Date(ts).toTimeString().slice(0, 5)`, which
 * reads the BROWSER's timezone. Every list on the page renders the same column through
 * fmtTime (company timezone), so an edit dialog opened from one of those rows showed a
 * different clock than the row it came from — for a company on America/New_York edited from
 * a machine on America/Los_Angeles, a 12:00 PM entry opened as 09:00. Nothing was corrupted
 * by that alone (the write used the same browser-local convention, so it round-tripped), but
 * a user who "corrects" the dialog to match the list shifts the entry by the offset.
 */
export const companyTimeKey = (ts) => {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', withTz({ hour: '2-digit', minute: '2-digit', hour12: false }));
};

/**
 * How far the company timezone is from UTC at a given instant, in milliseconds.
 * formatToParts rather than parsing toLocaleString: the parse is locale- and
 * engine-dependent, this is neither.
 */
const companyOffsetMsAt = (date) => {
  if (!companyTimeZone) return -date.getTimezoneOffset() * 60000;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: companyTimeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  // Some engines render midnight as hour '24'.
  const hour = parts.hour === '24' ? '00' : parts.hour;
  const asIfUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +hour, +parts.minute, +parts.second);
  return asIfUtc - date.getTime();
};

/**
 * A calendar date + wall-clock time the user typed, read as COMPANY-local, returned as a UTC
 * ISO string for a timestamptz column. The inverse of companyTimeKey.
 *
 * Board #3349. Use this instead of fromDateTimeLocalInput whenever the wall clock on screen
 * is company-local — which it is anywhere the surrounding page formats through fmtTime.
 *
 * @param {string} dateStr 'YYYY-MM-DD'
 * @param {string} timeStr 'HH:mm' (or 'HH:mm:ss')
 * @returns {string|null} ISO 8601 UTC, or null if either part is missing/unparseable
 */
export const companyWallClockToUtc = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const [hh, mm] = String(timeStr).split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => !Number.isFinite(n))) return null;
  const naiveAsUtc = Date.UTC(y, m - 1, d, hh, mm, 0);
  // One correction lands it; a second settles the hour either side of a DST change, where
  // the offset at the guessed instant differs from the offset at the real one.
  let ms = naiveAsUtc - companyOffsetMsAt(new Date(naiveAsUtc));
  ms = naiveAsUtc - companyOffsetMsAt(new Date(ms));
  return new Date(ms).toISOString();
};

const WALL_CLOCK_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/**
 * Value for an `<input type="datetime-local">` showing a timestamptz in COMPANY time.
 * A value that is already a wall-clock string (what the input itself produced) is passed
 * through. Board #4405: the Edit Job form used the browser-local toDateTimeLocalInput while the
 * list and job page render company time, so the same job showed two different start times.
 */
export const toCompanyDateTimeLocal = (value) => {
  if (!value) return '';
  if (WALL_CLOCK_LOCAL.test(String(value))) return String(value).slice(0, 16);
  const date = companyDateKey(value);
  const time = companyTimeKey(value);
  return date && time ? `${date}T${time}` : '';
};

/** Inverse of toCompanyDateTimeLocal: a wall-clock input value read as company time -> UTC ISO. */
export const companyDateTimeLocalToUtc = (value) => {
  if (!value) return null;
  const str = String(value);
  if (WALL_CLOCK_LOCAL.test(str)) return companyWallClockToUtc(str.slice(0, 10), str.slice(11, 16));
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const TIME_OPTIONS = { hour: 'numeric', minute: '2-digit' };
const DATE_SHORT_OPTIONS = { weekday: 'short', month: 'short', day: 'numeric' };
const DATE_FULL_OPTIONS = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
const DATE_LONG_OPTIONS = { year: 'numeric', month: 'long', day: 'numeric' };

/**
 * Format a timestamp to time string — "8:05 AM"
 * @param {string|Date} ts - ISO string or Date
 * @returns {string}
 */
export const fmtTime = (ts, options) => {
  if (!ts) return '';
  // hour12 is applied even when the caller passed options, because the 12/24-hour choice is
  // the company's and not the call site's — unless that call site said so explicitly.
  const base = options || TIME_OPTIONS;
  const withClock = companyTimeFormat && base.hour12 === undefined && base.hourCycle === undefined
    ? { ...base, hour12: !prefers24Hour() }
    : base;
  return new Date(ts).toLocaleTimeString('en-US', withTz(withClock));
};

/**
 * Format a timestamp to short date — "Mon, Feb 3", or "Mon, Feb 3, 2025" when the date is
 * not in the current year on the company calendar.
 * #6140 (ruled 2026-10-24): without the year, a February date read in October is ambiguous.
 * @param {string|Date} ts
 * @returns {string}
 */
export const fmtDateShort = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const sameYear = (companyDateKey(d) || '').slice(0, 4) === (companyDateKey() || '').slice(0, 4);
  return d.toLocaleDateString('en-US', withTz(sameYear ? DATE_SHORT_OPTIONS : DATE_FULL_OPTIONS));
};

/**
 * Format a timestamp to full date — "Mon, Feb 3, 2026"
 * @param {string|Date} ts
 * @returns {string}
 */
export const fmtDateFull = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', withTz(DATE_FULL_OPTIONS));
};

/**
 * Format a TIMESTAMP to a date string — "2/3/2026" by default.
 * Pass Intl options for a different shape (e.g. { weekday: 'long', month: 'long' }).
 *
 * For `date` COLUMNS (due_date, issue_date, expected_date …) use fmtDateOnly instead —
 * those have no instant and must not be timezone-converted.
 *
 * @param {string|Date} ts
 * @param {object} [options] Intl.DateTimeFormat options
 * @returns {string}
 */
export const fmtDate = (ts, options) => {
  if (!ts) return '';
  // Only the DEFAULT shape follows the company's date format. A caller passing explicit Intl
  // options has asked for a particular shape (a long month, a weekday) and overriding that
  // would break the surface that requested it.
  if (!options && companyDateFormat) {
    const { locale, options: preset } = DATE_FORMATS[companyDateFormat];
    return new Date(ts).toLocaleDateString(locale, withTz(preset));
  }
  return new Date(ts).toLocaleDateString('en-US', withTz(options || {}));
};

/** Numeric date options — "9/3/2026". Handy for fmtDateOnly on `date` columns. */
export const DATE_NUMERIC = { month: 'numeric', day: 'numeric', year: 'numeric' };

/**
 * Format a timestamp to a long date — "August 3, 2026".
 * The formal style used on customer-facing documents (quote/invoice emails, PDFs).
 * @param {string|Date} ts
 * @returns {string}
 */
export const fmtDateLong = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', withTz(DATE_LONG_OPTIONS));
};

/**
 * Format a DATE-ONLY column value — 'YYYY-MM-DD' → "September 3, 2026".
 *
 * Use this for `date` columns (invoices.due_date / issue_date, work_orders.invoice_date,
 * marketplace_requests.preferred_date …), NEVER fmtDate/fmtDateLong.
 *
 * A `date` has no instant behind it. `new Date('2026-09-03')` parses as UTC midnight, so
 * rendering it in ANY negative-offset zone yields September **2** — the invoice appears due
 * a day early. That is true for every US timezone, including the company one, so applying
 * the company timezone does not fix it either. The only correct handling is to treat the
 * value as naive: build the date from its own Y/M/D parts and never convert.
 *
 * Deliberately does NOT call withTz().
 *
 * RELATED: utils/dateUtils.parseDbDate() already does the same naive parse but returns a
 * Date rather than a string, so `parseDbDate(x).toLocaleDateString()` is equally correct and
 * existing call sites using it need no change. Use whichever fits; do NOT add a third.
 *
 * @param {string|Date} value 'YYYY-MM-DD' (an ISO timestamp is tolerated; the date part wins)
 * @param {object} [options] Intl options; defaults to the long form
 * @returns {string}
 */
export const fmtDateOnly = (value, options = DATE_LONG_OPTIONS) => {
  if (!value) return '';
  const [y, m, d] = String(value instanceof Date ? value.toISOString() : value)
    .slice(0, 10)
    .split('-')
    .map(Number);
  if (!y || !m || !d) return '';
  // A NUMERIC date is exactly what the company's Date Format setting governs (day/month order,
  // separators), so DATE_NUMERIC follows it the way fmtDate's default does. Other explicit shapes
  // (long month, weekday) stay as requested. Board #4257/#4637: DD/MM/YYYY was ignored here.
  if (options === DATE_NUMERIC && companyDateFormat) {
    const { locale, options: preset } = DATE_FORMATS[companyDateFormat];
    return new Date(y, m - 1, d).toLocaleDateString(locale, preset);
  }
  // Local construction: no UTC parse, so the calendar date cannot shift.
  return new Date(y, m - 1, d).toLocaleDateString('en-US', options);
};

/**
 * True when the company's TODAY is strictly after a `date` column value — i.e. the thing is
 * genuinely overdue.
 *
 * Use this for every overdue/past-due test on a `date` column. NEVER
 * `new Date(invoice.due_date) < new Date()`: that parses the due date as UTC midnight, so an
 * invoice due 2026-09-03 starts reading "overdue" at 2026-09-02 17:00 Pacific — more than a
 * full day early, and before the due day has even begun. It also compares an instant against
 * a calendar date, which is a category error.
 *
 * Pure string comparison on 'YYYY-MM-DD', so no timezone parsing is involved on either side
 * and there is nothing to get wrong.
 *
 * @param {string|Date} dateValue a `date` column value ('YYYY-MM-DD' or an ISO string)
 * @param {string|Date} [asOf] defaults to now
 * @returns {boolean}
 */
export const isPastDue = (dateValue, asOf) => {
  if (!dateValue) return false;
  const due = String(dateValue).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return false;
  const today = companyDateKey(asOf);
  return Boolean(today) && today > due;
};

/**
 * Smart "today/tomorrow/date" label + time
 * Returns: "Today · 8:05 AM" or "Tomorrow · 2:30 PM" or "Mon, Feb 3 · 8:05 AM"
 * @param {string|Date} ts
 * @returns {string}
 */
export const fmtSmartDateTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);

  // Compare on the COMPANY's calendar day. Using the browser's getFullYear/Month/Date meant
  // a job could read "Today" for one user and "Tomorrow" for another looking at the same job.
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const targetKey = companyDateKey(d);

  let dateLabel;
  if (targetKey === companyDateKey()) {
    dateLabel = 'Today';
  } else if (targetKey === companyDateKey(tomorrow)) {
    dateLabel = 'Tomorrow';
  } else {
    dateLabel = fmtDateShort(d);
  }

  return `${dateLabel} · ${fmtTime(d)}`;
};

/**
 * Format a schedule range — "Mon, Feb 3 · 8:05 AM – 10:30 AM"
 * If same day, omits end date. If different day, shows both.
 * @param {string|Date} start
 * @param {string|Date} end
 * @returns {string}
 */
export const fmtScheduleRange = (start, end) => {
  if (!start) return '';
  const s = new Date(start);
  const dateStr = fmtDateShort(s);
  const startTime = fmtTime(s);

  if (!end) return `${dateStr} · ${startTime}`;

  const e = new Date(end);
  const endTime = fmtTime(e);

  // Same day? Compared on the company's calendar day, not the browser's.
  if (companyDateKey(s) === companyDateKey(e)) {
    return `${dateStr} · ${startTime} – ${endTime}`;
  }

  return `${dateStr} · ${startTime} – ${fmtDateShort(e)} · ${endTime}`;
};

/**
 * Format duration in minutes to human string — "2h 30m" or "45m"
 * @param {number} minutes
 * @returns {string}
 */
export const fmtDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};
